import React from 'react';
import { useParams } from 'react-router-dom';

import { useDataTable } from '@/shared/components/ui/data-table/context/data-table-context';
import {
  TEntityActionType,
  TPermissionCheckProps,
} from '@/shared/features/ability/hooks/use-user-ability/use-user-ability.types';
import { AddResourcesToGroup } from '@/shared/features/groups/components/add-resources-to-group/add-resources-to-group';
import { UNGROUPED_RESOURCES_GROUP_ID } from '@/shared/features/groups/components/group-list/group-list.constants';
import { TGroupResourceBatchActionsProps } from '@/shared/features/groups/components/group-resource-batch-actions/group-resource-batch-actions.types';
import { MoveResources } from '@/shared/features/groups/components/move-resources/move-resources';
import { RemoveResourcesFromGroup } from '@/shared/features/groups/components/remove-resources-from-group/remove-resources-from-group';
import { useGroupsConfig } from '@/shared/features/groups/context/groups-config-context';
import { TGroupResourceItem } from '@/shared/features/groups/context/groups-config-context.types';

export const GroupResourceBatchActions: React.FC<
  TGroupResourceBatchActionsProps
> = ({ selectedItems, onSuccess }) => {
  const { groupId } = useParams();
  const { getItemId } = useDataTable();
  const { getPermissionEntity } = useGroupsConfig().useResourceElements();

  const getEntities = (): TEntityActionType[] => {
    const entities = selectedItems
      .map((item) => getPermissionEntity(item.__typename))
      .filter(Boolean);
    return Array.from(new Set(entities)) as TEntityActionType[];
  };

  const getAttribute = (item: TGroupResourceItem) => {
    const _groupId =
      groupId === UNGROUPED_RESOURCES_GROUP_ID ? undefined : groupId;

    return {
      id: getItemId(item),
      groupId: _groupId,
    };
  };

  const getPermission = (): TPermissionCheckProps | undefined => {
    if (!getEntities().length) {
      return undefined;
    }

    return {
      entity: getEntities(),
      action: 'update',
      attributes: selectedItems.map(getAttribute),
      relation: 'and',
    };
  };

  if (groupId === UNGROUPED_RESOURCES_GROUP_ID) {
    return (
      <AddResourcesToGroup
        resources={selectedItems}
        onSuccess={onSuccess}
        component="Button"
        permission={getPermission()}
      />
    );
  }

  return (
    <>
      <MoveResources
        component="Button"
        resources={selectedItems}
        onSuccess={onSuccess}
        permission={getPermission()}
      />
      <RemoveResourcesFromGroup
        component="Button"
        resources={selectedItems}
        onSuccess={onSuccess}
        permission={getPermission()}
      />
    </>
  );
};
